import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { chemistryApi } from '../../api/chemistry';
import { ChemFile } from '../../types/chemistry';
import NotificationBadge from '../ui/NotificationBadge';

const CHEM_EXT = new Set(['sdf','mol','mol2','smi','smiles','csv','xlsx','xls','cdx','cdxml','cml','inchi','rxn','rdf']);

export const cleanFileName = (name: string) => name.replace(/^[a-f0-9]{8}_/i, '');

const isChemFile = (f: ChemFile) => {
  if (f.fileType) return f.fileType === 'molecules';
  // No fileType set — fall back to extension check (backward compat for older files)
  // Check originalFileName first, then fileName (originalFileName may lack extension after rename)
  const origExt = (f.originalFileName || '').split('.').pop()?.toLowerCase();
  if (origExt && CHEM_EXT.has(origExt)) return true;
  const ext = (f.fileName || '').split('.').pop()?.toLowerCase();
  return !!ext && CHEM_EXT.has(ext);
};

export function getFileExtension(f: ChemFile): string {
  const name = f.originalFileName || f.fileName;
  const ext = name.split('.').pop()?.toLowerCase();
  return ext ? `.${ext}` : '';
}

function isFileNew(file: ChemFile, lastViewedAt: number): boolean {
  if (lastViewedAt === 0) return false;
  const modified = file.modifiedDate ? new Date(file.modifiedDate).getTime() : 0;
  const created = file.createdDate ? new Date(file.createdDate).getTime() : 0;
  return Math.max(modified, created) > lastViewedAt;
}

const LAST_VIEWED_KEY = 'projectLastViewedAt';
const PANEL_MODE_KEY = 'discoverant_panel_mode';
const FILE_LIST_VIEW_KEY = 'discoverant_file_list_view';

function getLastViewedAt(projectId: number): number {
  try {
    const stored = localStorage.getItem(LAST_VIEWED_KEY);
    if (!stored) return 0;
    const map = JSON.parse(stored);
    return map[String(projectId)] || 0;
  } catch { return 0; }
}

function loadPanelMode(): 'full' | 'slim' | 'hidden' {
  try {
    const v = localStorage.getItem(PANEL_MODE_KEY);
    if (v === 'slim' || v === 'hidden') return v;
  } catch { /* ignore */ }
  return 'full';
}

function loadFileListView(): 'list' | 'gallery' {
  try {
    const v = localStorage.getItem(FILE_LIST_VIEW_KEY);
    if (v === 'gallery') return v;
  } catch { /* ignore */ }
  return 'list';
}

const PANEL_WIDTHS = { full: 220, slim: 40, hidden: 0 } as const;

interface FilePanelProps {
  projectId: number;
  activeFileId?: string;
  /** Pre-fetched files — if provided, panel skips its own fetch */
  files?: ChemFile[];
  onWhatsNewClick?: () => void;
  onAllCompoundsClick?: () => void;
  onExpandToLevel2?: () => void;
  fileUnreadCounts?: Map<string, number>;
  totalUnreadCount?: number;
  externalNewFileIds?: Set<string>;
  totalCompoundCount?: number;
  duplicateCount?: number;
  isAllCompoundsActive?: boolean;
}

const FilePanel: React.FC<FilePanelProps> = ({
  projectId,
  activeFileId,
  files: propFiles,
  onWhatsNewClick,
  onAllCompoundsClick,
  onExpandToLevel2,
  fileUnreadCounts: externalUnreadCounts,
  totalUnreadCount: externalTotalUnread,
  externalNewFileIds,
  totalCompoundCount,
  duplicateCount,
  isAllCompoundsActive,
}) => {
  const navigate = useNavigate();

  const [fetchedFiles, setFetchedFiles] = useState<ChemFile[]>([]);
  const [isLoading, setIsLoading] = useState(!propFiles);
  const [panelMode, setPanelMode] = useState<'full' | 'slim' | 'hidden'>(loadPanelMode);
  const [fileListView, setFileListView] = useState<'list' | 'gallery'>(loadFileListView);
  const [lastViewedAt] = useState(() => getLastViewedAt(projectId));

  const files = propFiles ?? fetchedFiles;

  // Persist preferences
  useEffect(() => { localStorage.setItem(PANEL_MODE_KEY, panelMode); }, [panelMode]);
  useEffect(() => { localStorage.setItem(FILE_LIST_VIEW_KEY, fileListView); }, [fileListView]);

  // Ctrl+B cycles panel mode
  const cyclePanelMode = useCallback(() => {
    setPanelMode(prev => prev === 'full' ? 'slim' : prev === 'slim' ? 'hidden' : 'full');
  }, []);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'b') {
        e.preventDefault();
        cyclePanelMode();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [cyclePanelMode]);

  // Fetch files only when not provided via props
  useEffect(() => {
    if (propFiles) { setIsLoading(false); return; }
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      try {
        const allFiles = await chemistryApi.getFiles(projectId);
        if (!cancelled) setFetchedFiles(allFiles.filter(isChemFile));
      } catch { /* ignore */ }
      finally { if (!cancelled) setIsLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [projectId, propFiles]);

  // New file IDs
  const newFileIds = useMemo(() => {
    if (externalNewFileIds) return externalNewFileIds;
    const ids = new Set<string>();
    files.forEach(f => { if (isFileNew(f, lastViewedAt)) ids.add(f.id); });
    return ids;
  }, [files, lastViewedAt, externalNewFileIds]);

  // Per-file unread counts
  const fileUnreadCounts = useMemo(() => {
    if (externalUnreadCounts) return externalUnreadCounts;
    const counts = new Map<string, number>();
    files.forEach(f => {
      if (newFileIds.has(f.id)) counts.set(f.id, f.moleculesLibraryCount);
    });
    return counts;
  }, [externalUnreadCounts, files, newFileIds]);

  const totalUnreadCount = useMemo(() => {
    if (externalTotalUnread !== undefined) return externalTotalUnread;
    let total = 0;
    fileUnreadCounts.forEach(c => { total += c; });
    return total;
  }, [externalTotalUnread, fileUnreadCounts]);

  // Handlers
  const handleFileSelect = useCallback((fileId: string) => {
    console.log('FILE CLICK', fileId, 'navigating to', `/chemistry/file/${fileId}/editor`);
    navigate(`/chemistry/file/${fileId}/editor`);
  }, [navigate]);

  const handleWhatsNew = useCallback(() => {
    if (onWhatsNewClick) onWhatsNewClick();
    else navigate(`/chemistry/files?project=${projectId}&view=new`);
  }, [onWhatsNewClick, navigate, projectId]);

  const handleAllCompounds = useCallback(() => {
    if (onAllCompoundsClick) onAllCompoundsClick();
    else navigate(`/chemistry/file/project-${projectId}/editor`);
  }, [onAllCompoundsClick, navigate, projectId]);

  const handleExpand = useCallback(() => {
    if (onExpandToLevel2) onExpandToLevel2();
    else navigate(`/chemistry/files?project=${projectId}&view=files`);
  }, [onExpandToLevel2, navigate, projectId]);

  const panelWidth = PANEL_WIDTHS[panelMode];

  if (isLoading || files.length === 0) return null;

  return (
    <>
      {/* Hidden mode: reveal handle */}
      {panelMode === 'hidden' && (
        <button
          onClick={() => setPanelMode('full')}
          className="flex-shrink-0 w-5 flex items-center justify-center bg-gray-50 dark:bg-gray-900 border-r border-gray-100 dark:border-gray-800 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 transition-colors group"
          title="Show file panel (Ctrl+B)"
        >
          <svg className="w-3 h-3 text-gray-300 group-hover:text-emerald-500 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      )}

      {/* File panel */}
      <div
        className="flex-shrink-0 border-r border-gray-100 dark:border-gray-800 overflow-hidden bg-white dark:bg-gray-900 flex flex-col"
        style={{ width: panelWidth, minWidth: panelWidth, transition: 'width 200ms ease, min-width 200ms ease' }}
      >
        {panelMode === 'slim' && (
          <div className="flex flex-col h-full">
            <button onClick={() => setPanelMode('full')} className="mx-auto mt-2 mb-1 w-9 h-9 rounded-lg flex items-center justify-center bg-emerald-50 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-100 dark:hover:bg-emerald-800/60 transition-colors shadow-sm flex-shrink-0" title="Expand file panel">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
            </button>
            <div className="w-5 border-t border-gray-200 dark:border-gray-700 mx-auto mb-1" />

            <div className="flex flex-col items-center gap-0.5 py-2 flex-1 overflow-y-auto">
              {/* What's New icon */}
              <button onClick={handleWhatsNew} title={`What's New${totalUnreadCount > 0 ? ` (${totalUnreadCount})` : ''}`} className="w-8 h-8 rounded-md flex items-center justify-center relative transition-colors bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" /></svg>
                {totalUnreadCount > 0 && <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-red-500" />}
              </button>
              {/* All Compounds icon */}
              <button onClick={handleAllCompounds} title={`All Compounds${totalCompoundCount ? ` (${totalCompoundCount})` : ''}`} className={`w-8 h-8 rounded-md flex items-center justify-center relative transition-colors ${isAllCompoundsActive ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-600'}`}>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" /></svg>
                {(duplicateCount ?? 0) > 0 && <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-amber-500" />}
              </button>
              <div className="w-5 border-t border-gray-100 dark:border-gray-800 my-1" />

              {/* File icons */}
              {files.map(file => {
                const name = cleanFileName(file.originalFileName || file.fileName);
                const unread = fileUnreadCounts.get(file.id) || 0;
                const isActive = file.id === activeFileId;
                return (
                  <button key={file.id} onClick={() => handleFileSelect(file.id)} title={`${name}\n${file.moleculesLibraryCount} molecules${unread > 0 ? `\n${unread} new` : ''}\nClick to open in editor`} className={`w-8 h-8 rounded-md flex items-center justify-center relative transition-colors text-[11px] font-semibold ${isActive ? 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300' : 'text-gray-400 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-600 dark:hover:text-gray-200'}`}>
                    {name.charAt(0).toUpperCase()}
                    {unread > 0 && <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-red-500" />}
                  </button>
                );
              })}
            </div>

            <button onClick={() => setPanelMode('full')} className="p-2 flex items-center justify-center text-emerald-500 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 transition-colors border-t border-gray-200 dark:border-gray-700 flex-shrink-0" title="Expand file panel">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" /></svg>
            </button>
          </div>
        )}

        {panelMode === 'full' && (
          <>
            {/* Header bar */}
            <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100 dark:border-gray-800 flex-shrink-0">
              <div className="flex items-center gap-1">
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Files</span>
                <button onClick={() => setFileListView('list')} className={`p-0.5 rounded transition-colors ${fileListView === 'list' ? 'text-emerald-600' : 'text-gray-300 hover:text-gray-500'}`} title="List view">
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
                </button>
                <button onClick={() => setFileListView('gallery')} className={`p-0.5 rounded transition-colors ${fileListView === 'gallery' ? 'text-emerald-600' : 'text-gray-300 hover:text-gray-500'}`} title="Gallery view">
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zm10 0a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zm10 0a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" /></svg>
                </button>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={handleExpand} className="p-1 text-gray-400 hover:text-emerald-600 hover:bg-gray-50 dark:hover:bg-gray-800 rounded transition-colors" title="File overview">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" /></svg>
                </button>
                <button onClick={() => setPanelMode('slim')} className="p-1 text-gray-400 hover:text-emerald-600 hover:bg-gray-50 dark:hover:bg-gray-800 rounded transition-colors" title="Collapse panel (Ctrl+B)">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                </button>
              </div>
            </div>

            {/* Scrollable file list */}
            <div className="flex-1 overflow-y-auto">
              {/* What's New entry — pinned at top */}
              <button onClick={handleWhatsNew} className="w-full text-left px-3 py-2.5 border-b border-gray-100 dark:border-gray-800 transition-all duration-150 bg-gradient-to-r from-emerald-50/80 to-teal-50/80 dark:from-emerald-900/20 dark:to-teal-900/20">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 min-w-0">
                    <svg className="w-4 h-4 flex-shrink-0 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" /></svg>
                    <span className="text-[13px] font-medium text-emerald-700 dark:text-emerald-400">What's New</span>
                  </div>
                  {totalUnreadCount > 0 && <NotificationBadge count={totalUnreadCount} />}
                </div>
              </button>

              {/* All Compounds entry — pinned after What's New */}
              <button onClick={handleAllCompounds} className={`w-full text-left px-3 py-2.5 border-b border-gray-100 dark:border-gray-800 transition-all duration-150 ${isAllCompoundsActive ? 'bg-gradient-to-r from-blue-50/80 to-indigo-50/80 dark:from-blue-900/20 dark:to-indigo-900/20' : 'hover:bg-gray-50 dark:hover:bg-gray-800/50'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 min-w-0">
                    <svg className="w-4 h-4 flex-shrink-0 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" /></svg>
                    <span className={`text-[13px] font-medium ${isAllCompoundsActive ? 'text-blue-700 dark:text-blue-400' : 'text-gray-700 dark:text-gray-300'}`}>All Compounds</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {(duplicateCount ?? 0) > 0 && (
                      <span className="text-[10px] font-semibold text-amber-600 bg-amber-50 dark:bg-amber-900/30 dark:text-amber-400 px-1.5 py-0.5 rounded-full">{duplicateCount} matches</span>
                    )}
                    {totalCompoundCount != null && (
                      <span className="text-[10px] text-gray-400">{totalCompoundCount.toLocaleString()}</span>
                    )}
                  </div>
                </div>
              </button>

              {fileListView === 'list' ? (
                <div className="py-1">
                  {files.map(file => {
                    const name = cleanFileName(file.originalFileName || file.fileName);
                    const unread = fileUnreadCounts.get(file.id) || 0;
                    const isNew = newFileIds.has(file.id);
                    const isActive = file.id === activeFileId;
                    return (
                      <button key={file.id} onClick={() => handleFileSelect(file.id)} title={`${name} — click to open in editor`} className={`w-full text-left px-3 py-2 transition-all duration-150 border-l-2 hover:bg-gray-50 dark:hover:bg-gray-800/50 ${isActive ? 'border-l-emerald-500 bg-emerald-50/50 dark:bg-emerald-900/20' : 'border-l-transparent'}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 min-w-0">
                            <div className="relative flex-shrink-0">
                              <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                              {isNew && <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full bg-teal-500" />}
                            </div>
                            <span className={`text-[13px] truncate ${isNew ? 'font-bold' : 'font-medium'} ${isActive ? 'text-emerald-700 dark:text-emerald-300' : 'text-gray-700 dark:text-gray-300'}`}>{name}</span>
                          </div>
                          {unread > 0 && <NotificationBadge count={unread} />}
                        </div>
                        <div className="text-[11px] text-gray-400 mt-0.5 pl-[22px]">
                          {file.moleculesLibraryCount.toLocaleString()} molecules
                        </div>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-1.5 p-2">
                  {files.map(file => {
                    const name = cleanFileName(file.originalFileName || file.fileName);
                    const ext = getFileExtension(file);
                    const unread = fileUnreadCounts.get(file.id) || 0;
                    const isNew = newFileIds.has(file.id);
                    const isActive = file.id === activeFileId;
                    return (
                      <button key={file.id} onClick={() => handleFileSelect(file.id)} title={`${name} — click to open in editor`} className={`text-left p-2 rounded-lg transition-all duration-150 relative border ${isActive ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-700' : 'bg-gray-50 dark:bg-gray-800/50 border-transparent hover:border-gray-200 dark:hover:border-gray-700'}`}>
                        {isNew && <span className="absolute top-1 left-1 w-1.5 h-1.5 rounded-full bg-teal-500" />}
                        {unread > 0 && <div className="absolute top-1 right-1"><NotificationBadge count={unread} /></div>}
                        <div className={`text-[11px] truncate ${isNew ? 'font-bold' : 'font-medium'} ${isActive ? 'text-emerald-700 dark:text-emerald-300' : 'text-gray-700 dark:text-gray-300'}`}>{name}</div>
                        {ext && <div className="text-[9px] font-bold uppercase text-gray-400 mt-0.5">{ext.replace('.', '')}</div>}
                        <div className="text-[10px] text-gray-400 mt-1">
                          {file.moleculesLibraryCount} mols
                          {(file.moleculesSkipped ?? 0) > 0 && <span className="text-gray-500"> ({file.moleculesSkipped} skipped)</span>}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default FilePanel;
